import { ComponentType, useMemo } from 'react'
import { QueryClient, useQueryClient } from '@tanstack/react-query'
import { createBrowserRouter, LoaderFunctionArgs, RouterProvider } from 'react-router-dom'
import { paths } from '@/config/paths'
import AppRoot, { ErrorBoundary } from '@/pages/root'
import SettingsLayout from '@/components/layout/settingsLayout'
import { AdminRoot } from '@/pages/admin/root'
import { UserRoot } from '@/pages/user/root'
import { LoadingPage } from '@/pages/loading'

type ModuleWithLoader = {
  clientLoader?: (queryClient: QueryClient) => (params: LoaderFunctionArgs<unknown>) => Promise<unknown>
  default: ComponentType
}

const convert = (queryClient: QueryClient) => (m: ModuleWithLoader) => {
  const { clientLoader, default: Component, ...rest } = m
  return {
    ...rest,
    loader: clientLoader ? clientLoader(queryClient) : undefined,
    Component,
  }
}

const createAppRouter = (queryClient: QueryClient) =>
  createBrowserRouter(
    [
      {
        Component: AppRoot,
        ErrorBoundary: ErrorBoundary,
        children: [
          // User
          {
            Component: UserRoot,
            path: paths.app.root.path,
            children: [
              //Dashboard
              {
                path: paths.app.dashboard.path,
                lazy: () => import('@/pages/user/dashboard').then(convert(queryClient)),
              },
              //Locations
              {
                path: paths.app.locations.path,
                lazy: () => import('@/pages/user/location/listLocationsPage').then(convert(queryClient)),
              },
              //Stock
              {
                path: paths.app.stock.root.path,
                children: [
                  //Parts
                  {
                    path: paths.app.stock.parts.root.path,
                    children: [
                      {
                        path: paths.app.stock.parts.list.path,
                        lazy: () => import('@/pages/user/part/listPartPage').then(convert(queryClient)),
                      },
                      {
                        path: paths.app.stock.parts.new.path,
                        lazy: () => import('@/pages/user/part/createPartPage').then(convert(queryClient)),
                      },
                      {
                        path: paths.app.stock.parts.part.root.path,
                        children: [
                          {
                            path: paths.app.stock.parts.part.view.path,
                            lazy: () => import('@/pages/user/part/viewPartPage').then(convert(queryClient)),
                          },
                          {
                            path: paths.app.stock.parts.part.edit.path,
                            lazy: () => import('@/pages/user/part/editPartPage').then(convert(queryClient)),
                          },
                        ],
                      },
                    ],
                  },
                  //Scraps
                  {
                    path: paths.app.stock.scraps.root.path,
                    children: [
                      {
                        path: paths.app.stock.scraps.list.path,
                        lazy: () => import('@/pages/user/scrap/listScrapPage').then(convert(queryClient)),
                      },
                      {
                        path: paths.app.stock.scraps.new.path,
                        lazy: () => import('@/pages/user/scrap/createScrapPage').then(convert(queryClient)),
                      },
                      {
                        path: paths.app.stock.scraps.scrap.root.path,
                        children: [
                          {
                            path: paths.app.stock.scraps.scrap.view.path,
                            lazy: () => import('@/pages/user/scrap/scrapPage').then(convert(queryClient)),
                          },
                        ],
                      },
                    ],
                  },
                  //Boxes
                  {
                    path: paths.app.stock.boxes.root.path,
                    children: [
                      {
                        path: paths.app.stock.boxes.list.path,
                        lazy: () => import('@/pages/user/box/listBoxPage').then(convert(queryClient)),
                      },
                      {
                        path: paths.app.stock.boxes.import.path,
                        lazy: () => import('@/pages/user/box/importBoxPage').then(convert(queryClient)),
                      },
                      {
                        path: paths.app.stock.boxes.box.root.path,
                        children: [
                          {
                            path: paths.app.stock.boxes.box.view.path,
                            lazy: () => import('@/pages/user/box/viewBoxPage').then(convert(queryClient)),
                          },
                          {
                            path: paths.app.stock.boxes.box.edit.path,
                            lazy: () => import('@/pages/user/box/editBoxPage').then(convert(queryClient)),
                          },
                        ],
                      },
                    ],
                  },
                ],
              },
              //Package
              {
                path: paths.app.package.root.path,
                children: [
                  {
                    path: paths.app.package.view.path,
                    lazy: () => import('@/pages/user/package/packagePage').then(convert(queryClient)),
                  },
                ],
              },
              //Packaging
              {
                path: paths.app.packaging.root.path,
                children: [
                  {
                    path: paths.app.packaging.list.path,
                    lazy: () => import('@/pages/user/packaging/listPackagingPage').then(convert(queryClient)),
                  },
                  {
                    path: paths.app.packaging.new.path,
                    lazy: () => import('@/pages/user/packaging/packagingAddNewPage').then(convert(queryClient)),
                  },
                  {
                    path: paths.app.packaging.edit.path,
                    lazy: () => import('@/pages/user/packaging/packagingEdit').then(convert(queryClient)),
                  },
                ],
              },
              //Logistics
              {
                path: paths.app.logistics.root.path,
                children: [
                  {
                    path: paths.app.logistics.list.path,
                    lazy: () => import('@/pages/user/logistics/listLogisticsPage').then(convert(queryClient)),
                  },
                  {
                    path: paths.app.logistics.edit.path,
                    lazy: () => import('@/pages/user/logistics/logisticsEdit').then(convert(queryClient)),
                  },
                ],
              },
              //Received
              {
                path: paths.app.received.root.path,
                children: [
                  {
                    path: paths.app.received.list.path,
                    lazy: () => import('@/pages/user/received/listReceivedPage').then(convert(queryClient)),
                  },
                  {
                    path: paths.app.received.new.path,
                    lazy: () => import('@/pages/user/received/receivedAddNew').then(convert(queryClient)),
                  },
                  {
                    path: paths.app.received.edit.path,
                    lazy: () => import('@/pages/user/received/receivedEdit').then(convert(queryClient)),
                  },
                  {
                    path: paths.app.received.open.path,
                    lazy: () => import('@/pages/user/received/receivedOpen').then(convert(queryClient)),
                  },
                ],
              },
              //Deviations
              {
                path: paths.app.deviations.root.path,
                children: [
                  {
                    path: paths.app.deviations.list.path,
                    lazy: () => import('@/pages/user/deviation/listDeviationsPage').then(convert(queryClient)),
                  },
                  {
                    path: paths.app.deviations.edit.path,
                    lazy: () => import('@/pages/user/deviation/checkDeviationPage').then(convert(queryClient)),
                  },
                ],
              },
              //Incidents
              {
                path: paths.app.incidents.path,
                lazy: () => import('@/pages/user/incident/listIncidentsPage').then(convert(queryClient)),
              },
              //AllLocalPackages
              {
                path: paths.app.allLocalPackages.path,
                lazy: () => import('@/pages/user/package/listPackagesPage').then(convert(queryClient)),
              },
            ],
          },
          // Admin
          {
            Component: AdminRoot,
            path: paths.admin.root.path,
            children: [
              //Dashboard
              {
                path: paths.admin.dashboard.path,
                lazy: () => import('@/pages/admin/dashboard').then(convert(queryClient)),
              },
              //Parts
              {
                path: paths.admin.parts.root.path,
                children: [
                  {
                    path: paths.admin.parts.list.path,
                    lazy: () => import('@/pages/admin/part/listGlobalPartsPage').then(convert(queryClient)),
                  },
                  {
                    path: paths.admin.parts.part.root.path,
                    children: [
                      {
                        path: paths.admin.parts.part.view.path,
                        lazy: () => import('@/pages/user/part/viewPartPage').then(convert(queryClient)),
                      },
                      {
                        path: paths.admin.parts.part.edit.path,
                        lazy: () => import('@/pages/user/part/editPartPage').then(convert(queryClient)),
                      },
                    ],
                  },
                ],
              },
              //Packages
              {
                path: paths.admin.packages.root.path,
                children: [
                  {
                    path: paths.admin.packages.list.path,
                    lazy: () => import('@/pages/admin/packages/listGlobalPackagesPage').then(convert(queryClient)),
                  },
                  {
                    path: paths.admin.packages.view.path,
                    lazy: () => import('@/pages/user/package/packagePage').then(convert(queryClient)),
                  },
                ],
              },
              //Locations
              {
                path: paths.admin.locations.path,
                lazy: () => import('@/pages/admin/location/listLocationsPage').then(convert(queryClient)),
              },
              //Users
              {
                path: paths.admin.users.path,
                lazy: () => import('@/pages/admin/user/listUsersPage').then(convert(queryClient)),
              },

              //Incidents
              {
                path: paths.admin.incidents.path,
                lazy: () => import('@/pages/admin/incident/listGlobalIncidentsPage').then(convert(queryClient)),
              },
              //Statistics
              {
                path: paths.admin.statistics.path,
                lazy: () => import('@/pages/admin/statistics/listStatsPage').then(convert(queryClient)),
              },
              //Settings
              {
                path: paths.admin.settings.root.path,
                Component: SettingsLayout,
                children: [
                  {
                    path: paths.admin.settings.customProperties.path,
                    lazy: () => import('@/pages/admin/settings/customProperties/page').then(convert(queryClient)),
                  },
                ],
              },
            ],
          },
        ],
      },
      { path: '*', lazy: () => import('@/pages/notFoundPage').then(convert(queryClient)) },
    ],
    {
      future: {
        v7_relativeSplatPath: true,
      },
    },
  )

const AppRouter = () => {
  const queryClient = useQueryClient()

  const router = useMemo(() => createAppRouter(queryClient), [queryClient])

  return <RouterProvider router={router} future={{ v7_startTransition: true }} fallbackElement={<LoadingPage />} />
}

export { AppRouter }
